import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import AuthLayout from "../../components/AuthLayout";
import { PROD_ATG_API_URL } from "../../api/axiosInstance";

const GOOGLE_ICON_URL =
  "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg";

const GoogleArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path
      d="M2.5 10H15.5M10.5 4.5L16 10L10.5 15.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getErrorContent = (errorReason) => {
  if (errorReason === "not_staff") {
    return {
      title: "Access Denied",
      message:
        "Your Google account exists, but it is not authorized for brand admin access.",
      helper: "If this is unexpected, contact your administrator.",
    };
  }

  if (errorReason === "not_created") {
    return {
      title: "Account Not Found",
      message: "This Google account is not registered for brand admin access.",
      helper: "Please contact your administrator to create your account.",
    };
  }

  return {
    title: "Login Failed",
    message: "There was an error during admin login.",
    helper: "Please try again.",
  };
};

export default function BrandAdminLogin() {
  const { adminLoginMutation } = useAuth();
  const { mutate: adminLogin } = adminLoginMutation;
  const navigate = useNavigate();
  const location = useLocation();
  const [errorReason, setErrorReason] = useState(null);
  const hasHandledAuthCode = useRef(false);

  useEffect(() => {
    if (location.state?.successMessage) {
      navigate(location.pathname, { replace: true });
    }

    const params = new URLSearchParams(window.location.search);
    const authCode = params.get("auth_code");
    const unauthorized = params.get("unauthorized");
    const reason = params.get("reason");

    if (unauthorized) {
      setErrorReason(reason || "generic");
      return;
    }

    if (authCode && !hasHandledAuthCode.current) {
      hasHandledAuthCode.current = true;
      navigate(location.pathname, { replace: true });
      adminLogin({
        auth_code: authCode,
        setError: () => setErrorReason("generic"),
      });
    }
  }, [adminLogin, location.pathname, location.state, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}${window.location.pathname}`;
    window.location.href = `${PROD_ATG_API_URL}auth/oauth-login?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const errorContent = errorReason ? getErrorContent(errorReason) : null;

  return (
    <AuthLayout>
      {errorContent ? (
        <Box
          sx={{
            border: "1px solid #ffcdd2",
            background: "#ffebee",
            color: "#b71c1c",
            borderRadius: "12px",
            p: 2,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>
            {errorContent.title}
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
            {errorContent.message}
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: "20px", mt: 0.5 }}>
            {errorContent.helper}
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ mb: 3, textAlign: "left" }}>
        <Typography
          sx={{
            m: "0 0 8px",
            fontSize: 22,
            lineHeight: "28px",
            fontWeight: 700,
            color: "#0d0d0d",
          }}
        >
          Welcome to Brand Dashboard Admin
        </Typography>
        <Typography
          sx={{
            m: 0,
            fontSize: 14,
            lineHeight: "20px",
            color: "#5a5a5a",
          }}
        >
          Manage onboarding brands, campaigns, creators, and analytics from one
          place.
        </Typography>
      </Box>

      <Button
        type="button"
        onClick={handleGoogleLogin}
        disabled={adminLoginMutation.isPending}
        sx={{
          width: "100%",
          height: 48,
          border: "none",
          borderRadius: "8px",
          background: "#efefef",
          color: "#5a5a5a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "8px 16px",
          textTransform: "none",
          opacity: adminLoginMutation.isPending ? 0.7 : 1,
          cursor: adminLoginMutation.isPending ? "not-allowed" : "pointer",
          "&:hover": { background: "#efefef" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="img"
            sx={{ width: 24, height: 24 }}
            src={GOOGLE_ICON_URL}
            alt="Google"
          />
          <Typography
            sx={{ fontSize: 16, lineHeight: "20px", fontWeight: 500 }}
          >
            Login with Google
          </Typography>
        </Box>
        <Box
          sx={{
            width: 32,
            minWidth: 32,
            p: "6px",
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#5a5a5a",
          }}
        >
          <GoogleArrowIcon />
        </Box>
      </Button>
    </AuthLayout>
  );
}
